import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Seo from "../components/Seo";
import { resolveImageUrl } from "../admin/ImageUpload";
import { useCart } from "../context/CartContext";
import { useParentAuth } from "../context/ParentAuthContext";
import { fmtEur } from "./Boutique";
import { ShoppingBag, Trash2, Minus, Plus, Loader2, Tag, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BoutiquePanier = () => {
  const { items, setQty, removeItem, subtotal, count } = useCart();
  const { user: parent } = useParentAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [note, setNote] = useState("");

  const discount = promo?.discount || 0;
  const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);

  const applyPromo = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/shop/validate-promo", { code, subtotal });
      setPromo({ ...data, code: data.code });
      toast.success(`Code ${data.code} appliqué : -${fmtEur(data.discount)}`);
    } catch (e) {
      setPromo(null);
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally { setChecking(false); }
  };

  const pay = async () => {
    if (!items.length) return;
    if (!parent) {
      localStorage.setItem("elan_shop_redirect", "/boutique/panier");
      toast.info("Connectez-vous à votre espace famille pour régler votre commande");
      navigate("/espace-famille/login");
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/parent/shop/checkout", {
        items: items.map((x) => ({ productId: x.productId, variantId: x.variantId, qty: x.qty })),
        promoCode: promo?.code || null,
        originUrl: window.location.origin,
        note,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du paiement");
      setPaying(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 sm:py-16" data-testid="shop-cart-page">
      <Seo title="Panier" description="Votre panier boutique L'Élan Danse" />
      <Link to="/boutique" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-deep-rose mb-6" data-testid="cart-back-link">
        <ArrowLeft size={15} /> Continuer mes achats
      </Link>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-800 mb-8">Votre panier</h1>

      {items.length === 0 ? (
        <div className="py-16 text-center" data-testid="cart-empty">
          <ShoppingBag size={32} className="mx-auto text-stone-300 mb-3" />
          <p className="text-stone-500 italic mb-5">Votre panier est vide.</p>
          <Link to="/boutique" className="inline-flex items-center gap-2 bg-coral text-white px-6 py-3 rounded-full text-sm tracking-widest uppercase">Voir la boutique</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div className="space-y-3" data-testid="cart-items">
            {items.map((it) => (
              <div key={it.variantId} className="flex gap-4 bg-white border border-stone-200 rounded-2xl p-3" data-testid={`cart-item-${it.variantId}`}>
                <div className="h-20 w-20 rounded-xl overflow-hidden bg-stone-100 shrink-0">
                  {it.image ? <img src={resolveImageUrl(it.image)} alt={it.name} className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-serif-display text-base md:text-lg text-stone-800 truncate">{it.name}</h2>
                  <p className="text-xs text-stone-500">{[it.size, it.color].filter(Boolean).join(" · ") || "Taille unique"}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="inline-flex items-center border border-stone-200 rounded-full">
                      <button onClick={() => setQty(it.variantId, it.qty - 1)} className="p-2 text-stone-500 hover:text-deep-rose" data-testid={`cart-minus-${it.variantId}`}><Minus size={13} /></button>
                      <span className="w-7 text-center text-sm">{it.qty}</span>
                      <button onClick={() => setQty(it.variantId, it.qty + 1)} className="p-2 text-stone-500 hover:text-deep-rose" data-testid={`cart-plus-${it.variantId}`}><Plus size={13} /></button>
                    </div>
                    <button onClick={() => removeItem(it.variantId)} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50" data-testid={`cart-remove-${it.variantId}`}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-deep-rose font-medium">{fmtEur(it.unitPrice * it.qty)}</div>
                  <div className="text-xs text-stone-400">{fmtEur(it.unitPrice)} / unité</div>
                </div>
              </div>
            ))}

            <div className="bg-white border border-stone-200 rounded-2xl p-3">
              <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Note pour l&apos;école (facultatif)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="cart-note" placeholder="Ex : à remettre au cours du mardi" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush" />
            </div>
          </div>

          <aside className="bg-white border border-stone-200 rounded-2xl p-4 lg:sticky lg:top-24" data-testid="cart-summary">
            <h2 className="font-serif-display text-lg text-stone-800 mb-4">Récapitulatif</h2>
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Code promo" data-testid="cart-promo-input" className="w-full bg-stone-50 border border-stone-200 rounded-lg pl-8 pr-2 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blush" />
              </div>
              <button onClick={applyPromo} disabled={checking} className="px-3 py-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm disabled:opacity-50" data-testid="cart-promo-apply">
                {checking ? <Loader2 className="animate-spin" size={14} /> : "OK"}
              </button>
            </div>

            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-stone-500">Articles ({count})</dt><dd data-testid="cart-subtotal">{fmtEur(subtotal)}</dd></div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald-700"><dt>Remise {promo?.code}</dt><dd data-testid="cart-discount">- {fmtEur(discount)}</dd></div>
              )}
              <div className="flex justify-between"><dt className="text-stone-500">Retrait à l&apos;école</dt><dd className="text-stone-500">Gratuit</dd></div>
              <div className="flex justify-between border-t border-stone-100 pt-2.5 text-base">
                <dt className="font-medium text-stone-800">Total</dt>
                <dd className="font-medium text-deep-rose" data-testid="cart-total">{fmtEur(total)}</dd>
              </div>
            </dl>

            <button onClick={pay} disabled={paying} data-testid="cart-pay-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-coral text-white px-6 py-3.5 rounded-full text-sm tracking-widest uppercase hover:brightness-105 transition-all disabled:opacity-50">
              {paying ? <Loader2 className="animate-spin" size={15} /> : <Lock size={14} />} Payer par carte
            </button>
            {!parent && (
              <p className="text-xs text-stone-500 mt-3 text-center" data-testid="cart-login-hint">
                La connexion à votre espace famille sera demandée avant le paiement.
              </p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default BoutiquePanier;
